'use client';

import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'motion/react';
import { Sparkle, Cursor, ArrowsClockwise } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';

/**
 * Looping demo carousel for the empty-state homepage. Shows real Galexia
 * outputs (videos + a 3D scene still) and lets the user click to drop the
 * generating prompt into the input — single click → "see Galexia recreate
 * this for me".
 *
 * UX notes:
 *  - First demo loads with preload='auto' for instant first paint; the rest
 *    use 'metadata' so we don't burn bandwidth.
 *  - Videos auto-advance on `ended` (no arbitrary timer). Images advance
 *    after a fixed 6s.
 *  - Cross-fade between slides via AnimatePresence to avoid the jarring
 *    swap that comes with replacing src on the same <video>.
 *  - Click-to-prefill on the entire surface — large hit target.
 */

interface DemoSlide {
  kind: 'video' | 'image';
  src: string;
  prompt: string;
  /** Short label shown on the slide while playing. */
  label: string;
}

const DEMOS: DemoSlide[] = [
  {
    kind: 'video',
    src: '/demos/stars.mp4',
    prompt: 'Make a 2-minute video about the different kinds of stars',
    label: 'A narrated video, generated end-to-end',
  },
  {
    kind: 'image',
    src: '/demos/heart-3d.png',
    prompt: 'Show me an interactive 3D model of the human heart',
    label: 'A spinnable 3D scene',
  },
  {
    kind: 'video',
    src: '/demos/ctc.mp4',
    prompt: 'Explain CTC training in speech recognition',
    label: 'A technical explainer',
  },
  {
    kind: 'video',
    src: '/demos/internet-history.mp4',
    prompt: 'Make a video about the history of the internet',
    label: 'A timeline brought to life',
  },
];

const IMAGE_DWELL_MS = 6000;

interface HomepageDemoProps {
  onPrefill: (prompt: string) => void;
  className?: string;
}

export function HomepageDemo({ onPrefill, className }: HomepageDemoProps) {
  const [idx, setIdx] = useState(0);
  const slide = DEMOS[idx];
  const next = () => setIdx((i) => (i + 1) % DEMOS.length);
  const goTo = (i: number) => setIdx(i);
  // Visitors with prefers-reduced-motion get a static slide — pagination
  // dots still let them browse manually. Satisfies WCAG 2.2.2 by simply
  // never auto-advancing or autoplaying.
  const reducedMotion = useReducedMotion();

  // Image slides auto-advance via timer (skipped when reduced-motion).
  const imageTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (imageTimerRef.current) clearTimeout(imageTimerRef.current);
    if (reducedMotion) return;
    if (slide.kind === 'image') {
      imageTimerRef.current = setTimeout(next, IMAGE_DWELL_MS);
    }
    return () => {
      if (imageTimerRef.current) clearTimeout(imageTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, reducedMotion]);

  return (
    <div
      className={cn(
        'group relative w-full max-w-2xl overflow-hidden rounded-2xl border border-violet-500/20 bg-black/40 shadow-2xl shadow-violet-500/10',
        className,
      )}
      style={{ aspectRatio: '16 / 9' }}
      role="region"
      aria-roledescription="carousel"
      aria-label="Galexia demo carousel"
    >
      {/* Aurora glow behind slides */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-40"
        style={{
          background:
            'radial-gradient(ellipse at 30% 20%, rgba(139,92,246,0.35) 0%, transparent 60%), radial-gradient(ellipse at 70% 80%, rgba(6,182,212,0.25) 0%, transparent 60%)',
        }}
      />

      <AnimatePresence mode="wait">
        <motion.button
          key={slide.src}
          type="button"
          onClick={() => onPrefill(slide.prompt)}
          initial={{ opacity: 0, scale: 1.02 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.98 }}
          transition={{ duration: 0.45, ease: [0.22, 0.61, 0.36, 1] }}
          className="absolute inset-0 block w-full text-left"
          aria-roledescription="slide"
          aria-label={`Slide ${idx + 1} of ${DEMOS.length}, ${slide.label}: try the prompt "${slide.prompt}"`}
        >
          {slide.kind === 'video' ? (
            <video
              key={slide.src}
              src={slide.src}
              autoPlay={!reducedMotion}
              muted
              playsInline
              preload={idx === 0 ? 'auto' : 'metadata'}
              onEnded={next}
              onError={next}
              className="absolute inset-0 size-full object-cover"
            />
          ) : (
            <motion.img
              src={slide.src}
              alt={slide.prompt}
              className="absolute inset-0 size-full object-cover"
              initial={{ scale: reducedMotion ? 1 : 1.04 }}
              animate={{ scale: reducedMotion ? 1 : 1.12 }}
              transition={{ duration: IMAGE_DWELL_MS / 1000, ease: 'easeOut' }}
            />
          )}

          {/* Bottom gradient + caption */}
          <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent p-4 sm:p-5">
            <div className="flex items-end justify-between gap-3">
              <div className="min-w-0">
                <div className="mb-1 flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-wider text-violet-200/80">
                  <Sparkle weight="fill" className="size-3 text-violet-300" />
                  <span>{slide.label}</span>
                </div>
                <p className="truncate text-sm sm:text-base font-medium text-white/95">
                  &ldquo;{slide.prompt}&rdquo;
                </p>
              </div>
              <span className="hidden shrink-0 items-center gap-1 rounded-full border border-white/15 bg-white/5 px-2.5 py-1 text-[10px] font-medium text-white/80 backdrop-blur-sm group-hover:flex">
                <Cursor weight="fill" className="size-3" />
                Click to try
              </span>
            </div>
          </div>
        </motion.button>
      </AnimatePresence>

      {/* Bottom-right: pagination dots. Using plain buttons with aria-current
          rather than role="tab", since the slides aren't tab-panels — they're
          a single auto-advancing image stack inside an aria-roledescription
          carousel. The WAI-ARIA carousel pattern uses plain navigation
          buttons here, not tabs. */}
      <div
        className="absolute bottom-3 right-3 flex items-center gap-1.5 rounded-full border border-white/10 bg-black/30 px-2 py-1 backdrop-blur-sm"
        aria-label="Demo carousel pagination"
      >
        {DEMOS.map((d, i) => {
          const isActive = i === idx;
          return (
            <button
              key={i}
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                goTo(i);
              }}
              aria-current={isActive ? 'true' : undefined}
              aria-label={`Show demo ${i + 1} of ${DEMOS.length}: ${d.label}`}
              className={cn(
                'size-1.5 rounded-full transition-all',
                isActive ? 'w-4 bg-white' : 'bg-white/40 hover:bg-white/70',
              )}
            />
          );
        })}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            next();
          }}
          aria-label="Next demo"
          className="ml-1 inline-flex size-4 items-center justify-center rounded-full text-white/60 transition-colors hover:text-white"
        >
          <ArrowsClockwise weight="bold" className="size-3" />
        </button>
      </div>
    </div>
  );
}
